<template>
  <el-popover
    :visible="popoverVisible"
    placement="bottom-end"
    :width="360"
    trigger="manual"
    popper-class="p-0"
    @show="onPopoverShow"
    @hide="onPopoverHide"
  >
    <template #reference>
      <div
        class="relative cursor-pointer select-none flex items-center justify-center w-10 h-10 hover:opacity-75 transition-opacity"
        @click.stop="popoverVisible = !popoverVisible"
      >
        <font-awesome-icon icon="bell" class="text-xl text-[#2A4B72]" />
        <span
          v-if="unreadCount > 0"
          class="absolute -top-1 -end-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] pt-1 px-1"
          style="display:flex;align-items:center;justify-content:center;line-height:1;font-size:10px;"
        >
          {{ unreadCount > 99 ? '99+' : unreadCount }}
        </span>
      </div>
    </template>

    <!-- Outer wrapper: click.stop prevents document listener from closing the popover -->
    <div
      class="flex flex-col rounded-lg"
      :class="currentView === 'list' ? 'overflow-hidden' : 'overflow-visible'"
      style="max-height: 520px;"
      :dir="locale === 'ar' ? 'rtl' : 'ltr'"
      @click.stop
    >
      <!-- ── LIST VIEW ── -->
      <template v-if="currentView === 'list'">
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 border-b bg-white sticky top-0">
          <div class="flex items-center gap-2">
            <span class="font-bold text-base text-[#0B1F3B]">{{ $t('views.notifications.title') }}</span>
            <span
              v-if="unreadCount > 0"
              class="bg-red-100 text-red-600 text-xs font-semibold px-2 py-0.5 rounded-full"
            >
              {{ unreadCount }} {{ $t('views.notifications.unread') }}
            </span>
          </div>
          <div class="flex items-center gap-2">
            <LoadingSpinner v-if="loading" diameter="18px" />
            <!-- + Add reminder button -->
            <button
              class="flex items-center justify-center w-6 h-6 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-800"
              :title="$t('views.notifications.reminder.addTitle')"
              @click.stop="openAddReminder"
            >
              <Icon icon="mdi:plus-circle-outline" class="text-xl" />
            </button>
            <button
              v-if="unreadCount > 0"
              class="text-xs text-blue-600 hover:text-blue-800 transition-colors whitespace-nowrap"
              @click.stop="markAllRead"
            >
              {{ $t('views.notifications.markAllRead') }}
            </button>
          </div>
        </div>

        <!-- List -->
        <div class="overflow-y-auto flex-1 bg-white">
          <div
            v-if="!loading && notifications.length === 0"
            class="text-center text-gray-400 py-10 text-sm"
          >
            {{ $t('views.notifications.empty') }}
          </div>

          <div
            v-for="item in notifications"
            :key="`${item.entityType}-${item.entityID}`"
            class="flex items-center gap-3 px-4 py-3 border-b last:border-b-0 transition-colors hover:bg-gray-50"
            :class="item.entityType === 'reminder' ? 'hover:brightness-95' : (!item.isRead ? 'bg-red-50 hover:bg-red-50' : '')"
            :style="item.entityType === 'reminder' ? { backgroundColor: '#FEF6C6' } : {}"
          >
            <!-- Unread dot -->
            <div
              class="flex-shrink-0 w-2 h-2 rounded-full transition-colors"
              :class="item.isRead ? 'bg-transparent' : 'bg-red-500'"
            />

            <!-- Type icon — cursor-pointer for reminders to indicate clickable -->
            <div
              class="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
              :class="[typeIconBg(item.entityType), item.entityType === 'reminder' ? 'cursor-pointer' : '']"
              :style="item.entityType === 'reminder' ? { backgroundColor: '#FECF08' } : {}"
              @click="item.entityType === 'reminder' ? openReminderEdit(item) : onNotificationClick(item)"
            >
              <Icon v-if="item.entityType === 'reminder'" icon="mdi:clipboard-list" class="text-white text-sm" />
              <font-awesome-icon v-else :icon="typeIcon(item.entityType)" class="text-white text-xs" />
            </div>

            <!-- Text — always cursor-pointer -->
            <div
              class="flex-1 min-w-0 cursor-pointer"
              @click="item.entityType === 'reminder' ? openReminderEdit(item) : onNotificationClick(item)"
            >
              <div class="flex items-center gap-1.5">
                <span class="text-sm font-semibold text-[#0B1F3B]">
                  {{ item.entityType === 'reminder' && item.title ? item.title : $t(`views.notifications.types.${item.entityType}`) }}
                </span>
                <span
                  v-if="item.entityType === 'reminder' && item.repeatType"
                  class="text-xs font-medium text-blue-600 capitalize"
                >
                  {{ $t(`views.notifications.repeat.${item.repeatType}`) }}
                </span>
              </div>
              <div class="text-xs text-gray-500 truncate">{{ item.subTitle }}</div>
            </div>

            <!-- Time (non-quote, non-birthday, non-order-due) -->
            <div
              v-if="item.entityType !== 'quote' && item.entityType !== 'birthday' && item.entityType !== 'order_due'"
              class="flex-shrink-0 text-xs text-gray-400 whitespace-nowrap"
              dir="ltr"
            >
              {{ formatTime(item.date) }}
            </div>

            <!-- Reminder: Done button only (delete is on the full page) -->
            <template v-if="item.entityType === 'reminder'">
              <button
                class="flex-shrink-0 text-xs font-medium px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors whitespace-nowrap"
                @click.stop="onMarkReminderDone(item)"
              >
                {{ $t('views.notifications.reminder.markDone') }}
              </button>
            </template>
          </div>
        </div>

        <!-- Footer -->
        <div class="border-t bg-white px-4 py-2">
          <router-link
            to="/notifications"
            class="block text-center text-sm text-gray-900 hover:text-black transition-colors"
            @click="popoverVisible = false"
          >
            {{ $t('views.notifications.viewAll') }}
          </router-link>
        </div>
      </template>

      <!-- ── ADD REMINDER VIEW ── -->
      <template v-else>
        <!-- Header -->
        <div class="flex items-center gap-2 px-4 py-3 border-b bg-white">
          <button
            class="text-gray-400 hover:text-gray-600 transition-colors"
            @click.stop="currentView = 'list'"
          >
            <Icon icon="mdi:arrow-left" class="text-lg" />
          </button>
          <span class="font-bold text-base text-[#0B1F3B]">{{ $t('views.notifications.reminder.addTitle') }}</span>
        </div>

        <!-- Inline form: overflow-visible so non-teleported dropdowns render outside the container -->
        <div class="overflow-visible bg-white px-4 py-4 flex flex-col gap-3">
          <!-- Title -->
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">
              {{ $t('views.notifications.reminder.titleLabel') }} <span class="text-red-500">*</span>
            </label>
            <el-input ref="titleInputRef" v-model="inlineForm.title" size="small" />
            <p v-if="inlineErrors.title" class="text-xs text-red-500 mt-0.5">{{ inlineErrors.title }}</p>
          </div>

          <!-- Note -->
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">{{ $t('views.notifications.reminder.noteLabel') }}</label>
            <el-input v-model="inlineForm.note" type="textarea" :rows="2" size="small" />
          </div>

          <!-- Date & Time -->
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">
              {{ $t('views.notifications.reminder.dateTimeLabel') }} <span class="text-red-500">*</span>
            </label>
            <el-date-picker
              v-model="inlineForm.remindAt"
              type="datetime"
              size="small"
              class="w-full"
              format="YYYY-MM-DD hh:mm A"
              value-format="YYYY-MM-DDTHH:mm:ssZ"
              :teleported="false"
            />
            <p v-if="inlineErrors.remindAt" class="text-xs text-red-500 mt-0.5">{{ inlineErrors.remindAt }}</p>
          </div>

          <!-- Repeat -->
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">{{ $t('views.notifications.reminder.repeatLabel') }}</label>
            <el-select v-model="inlineForm.repeatType" size="small" class="w-full" :teleported="false">
              <el-option value=""        :label="$t('views.notifications.repeat.none')" />
              <el-option value="hourly"  :label="$t('views.notifications.repeat.hourly')" />
              <el-option value="daily"   :label="$t('views.notifications.repeat.daily')" />
              <el-option value="weekly"  :label="$t('views.notifications.repeat.weekly')" />
              <el-option value="monthly" :label="$t('views.notifications.repeat.monthly')" />
            </el-select>
          </div>
        </div>

        <!-- Footer -->
        <div class="border-t bg-white flex justify-end gap-2 px-4 py-3">
          <el-button size="small" @click.stop="currentView = 'list'">{{ $t('dialogs.cancel') }}</el-button>
          <el-button size="small" type="primary" :loading="inlineSaving" @click.stop="onInlineSave">OK</el-button>
        </div>
      </template>
    </div>

    <!-- Edit reminder dialog (outside the popover content, rendered via teleport) -->
    <ReminderDialog
      v-model="reminderDialogVisible"
      :reminder="selectedReminder"
      @saved="onReminderSaved"
      @deleted="onReminderSaved"
    />
  </el-popover>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Icon } from '@iconify/vue'
import { useHTTP } from '@/plugins/http'
import Notification, { type NotificationEntityType } from '@/models/notification/Notification'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import ReminderDialog from '@/views/notifications/ReminderDialog.vue'
import { formatPostgresDate } from '@/utils/date'

export default defineComponent({
  name: 'NotificationBell',
  components: { LoadingSpinner, Icon, ReminderDialog },

  setup() {
    const { t, locale }  = useI18n()
    const http           = useHTTP()
    const router         = useRouter()
    const popoverVisible = ref(false)
    const loading        = ref(false)
    const notifications  = ref<Notification[]>([])
    const currentView    = ref<'list' | 'add-reminder'>('list')
    const titleInputRef  = ref<{ focus: () => void } | null>(null)

    const inlineForm   = ref({ title: '', note: '', remindAt: '' as string, repeatType: '' })
    const inlineErrors = ref({ title: '', remindAt: '' })
    const inlineSaving = ref(false)

    const reminderDialogVisible = ref(false)
    const selectedReminder      = ref<Notification | null>(null)

    // Reminders are always isRead=false so they count toward badge
    const unreadCount = computed(() => notifications.value.filter(n => !n.isRead).length)

    const fetchNotifications = async () => {
      loading.value = true
      try {
        const data = await http.get<Notification[]>(Notification.ENDPOINT_TODAY)
        notifications.value = (data ?? []).map(Notification.from)
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      } catch {
        // fail silently
      } finally {
        loading.value = false
      }
    }

    const onPopoverShow = async () => {
      await fetchNotifications()
    }

    const onPopoverHide = () => {
      currentView.value = 'list'
    }

    // Close on outside clicks (only when in list mode; in add-reminder mode we stay open
    // so el-select / el-date-picker dropdowns don't dismiss the popover)
    const onDocumentClick = () => {
      if (popoverVisible.value && currentView.value === 'list' && !reminderDialogVisible.value) {
        popoverVisible.value = false
      }
    }

    onMounted(() => {
      fetchNotifications()
      document.addEventListener('click', onDocumentClick)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', onDocumentClick)
    })

    const markAllRead = () => {
      notifications.value.forEach(n => { if (n.entityType !== 'reminder') n.isRead = true })
      http.post(Notification.ENDPOINT_MARK_ALL, {}).catch(() => {})
    }

    const onNotificationClick = (item: Notification) => {
      item.isRead = true
      http.post(Notification.ENDPOINT_MARK_READ, {
        entityType: item.entityType,
        entityID:   item.entityID,
      }).catch(() => {})

      if (item.entityType === 'task') {
        router.push({ path: '/hr/tasks', query: { open_task: item.entityID } })
      } else {
        const path = item.navPath()
        if (path) router.push(path)
      }

      popoverVisible.value = false
    }

    const openReminderEdit = (item: Notification) => {
      if (!item.isRead) {
        item.isRead = true
        http.post(Notification.ENDPOINT_MARK_READ, {
          entityType: item.entityType,
          entityID:   item.entityID,
        }).catch(() => {})
      }
      selectedReminder.value = item
      reminderDialogVisible.value = true
    }

    const openAddReminder = () => {
      inlineForm.value = { title: '', note: '', remindAt: '', repeatType: '' }
      inlineErrors.value = { title: '', remindAt: '' }
      currentView.value = 'add-reminder'
      nextTick(() => {
        titleInputRef.value?.focus()
      })
    }

    const onMarkReminderDone = async (item: Notification) => {
      try {
        await http.post(`${Notification.ENDPOINT_REMINDERS}/${item.entityID}/done`, {})
        await fetchNotifications()
      } catch {
        // fail silently
      }
    }

    const onReminderSaved = () => {
      fetchNotifications()
    }

    const onInlineSave = async () => {
      inlineErrors.value = { title: '', remindAt: '' }
      let ok = true
      if (!inlineForm.value.title.trim()) {
        inlineErrors.value.title = t('views.notifications.reminder.titleLabel') + ' is required'
        ok = false
      }
      if (!inlineForm.value.remindAt) {
        inlineErrors.value.remindAt = t('views.notifications.reminder.dateTimeLabel') + ' is required'
        ok = false
      }
      if (!ok) return

      inlineSaving.value = true
      try {
        await http.post(Notification.ENDPOINT_REMINDERS, {
          title:      inlineForm.value.title.trim(),
          note:       inlineForm.value.note.trim(),
          remindAt:   inlineForm.value.remindAt,
          repeatType: inlineForm.value.repeatType,
        })
        inlineForm.value = { title: '', note: '', remindAt: '', repeatType: '' }
        currentView.value = 'list'
        await fetchNotifications()
      } catch {
        // error handled by http plugin
      } finally {
        inlineSaving.value = false
      }
    }

    const typeIcon = (entityType: NotificationEntityType): string => {
      switch (entityType) {
        case 'appointment': return 'calendar-day'
        case 'task':        return 'list'
        case 'birthday':    return 'user'
        case 'quote':       return 'receipt'
        case 'order_due':   return 'file-invoice'
        default:            return 'bell'
      }
    }

    const typeIconBg = (entityType: NotificationEntityType): string => {
      switch (entityType) {
        case 'appointment': return 'bg-blue-500'
        case 'task':        return 'bg-purple-500'
        case 'birthday':    return 'bg-pink-500'
        case 'quote':       return 'bg-green-600'
        case 'order_due':   return 'bg-orange-500'
        case 'reminder':    return ''
        default:            return 'bg-gray-400'
      }
    }

    const formatTime = (dateStr: string): string => {
      if (!dateStr) return ''
      return formatPostgresDate(dateStr, 'hh:mm A')
    }

    return {
      locale,
      popoverVisible,
      loading,
      notifications,
      currentView,
      titleInputRef,
      inlineForm,
      inlineErrors,
      inlineSaving,
      reminderDialogVisible,
      selectedReminder,
      unreadCount,
      onPopoverShow,
      onPopoverHide,
      markAllRead,
      onNotificationClick,
      openReminderEdit,
      openAddReminder,
      onMarkReminderDone,
      onReminderSaved,
      onInlineSave,
      typeIcon,
      typeIconBg,
      formatTime,
    }
  },
})
</script>
